import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Logo } from '../components/Logo';
import { api } from '../lib/api';
import { Check, AlertCircle, Loader2 } from 'lucide-react';

export default function CheckoutSuccess() {
    const { t } = useTranslation();
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const sessionId = params.get('session_id');
    const [status, setStatus] = useState('checking'); // checking|paid|failed|expired
    const [attempts, setAttempts] = useState(0);

    useEffect(() => {
        if (!sessionId) {
            setStatus('failed');
            return;
        }
        let cancelled = false;
        const maxAttempts = 10;
        const poll = async (n) => {
            if (cancelled || n >= maxAttempts) {
                if (!cancelled) setStatus('failed');
                return;
            }
            try {
                const res = await api.get(`/payments/checkout/status/${sessionId}`);
                if (res.data.payment_status === 'paid') {
                    setStatus('paid');
                    return;
                }
                if (res.data.status === 'expired') {
                    setStatus('expired');
                    return;
                }
                setAttempts(n + 1);
                setTimeout(() => poll(n + 1), 2000);
            } catch (e) {
                setStatus('failed');
            }
        };
        poll(0);
        return () => { cancelled = true; };
    }, [sessionId]);

    return (
        <div className="min-h-screen bg-[hsl(0_0%_98%)] flex flex-col">
            <div className="p-6 border-b bg-white"><Logo /></div>
            <div className="flex-1 flex items-center justify-center px-4">
                <Card className="max-w-lg w-full p-8 text-center">
                    {status === 'checking' && (
                        <>
                            <Loader2 className="w-12 h-12 mx-auto animate-spin text-[hsl(var(--accent))] mb-4" />
                            <h1 className="font-display text-3xl mb-2">Stiamo verificando il pagamento…</h1>
                            <p className="text-sm text-[hsl(var(--muted-foreground))]">Attendi qualche secondo. Tentativo {attempts + 1}/10.</p>
                        </>
                    )}
                    {status === 'paid' && (
                        <>
                            <div className="w-16 h-16 mx-auto bg-[hsl(145_55%_34%)] rounded-full flex items-center justify-center mb-4">
                                <Check className="w-8 h-8 text-white" />
                            </div>
                            <h1 className="font-display text-4xl mb-2">{t('pages.co_success_title')}</h1>
                            <p className="text-[hsl(var(--muted-foreground))] mb-6">{t('pages.co_success_msg')}</p>
                            <div className="flex gap-3 justify-center">
                                <Link to="/login"><Button className="bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] font-bold h-11 px-6" data-testid="checkout-success-login">{t('pages.co_success_cta')}</Button></Link>
                            </div>
                        </>
                    )}
                    {(status === 'failed' || status === 'expired') && (
                        <>
                            <AlertCircle className="w-12 h-12 mx-auto text-[hsl(var(--destructive))] mb-4" />
                            <h1 className="font-display text-3xl mb-2">Pagamento non riuscito</h1>
                            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6">Riprova o contatta il supporto.</p>
                            <Link to="/checkout"><Button>Riprova</Button></Link>
                        </>
                    )}
                </Card>
            </div>
        </div>
    );
}
